import { rootApi } from '../rootApi';
import { HTTPRequestType, PaginatedResponse } from '../api.types';
import {
  CallList,
  LogCallDto,
  EntryMoved,
  LoggedCall,
  CallQueueView,
  CallListEntry,
  CallListSummary,
  CreateCallListDto,
  UpdateCallListDto,
  CallListPersonType,
  CallListEntryDetail,
  CallListMemberships,
  AddCallListEntriesDto,
  UpdateCallListEntryDto,
  AddCallListEntriesResult,
} from './call-lists.types';

// Anything that changes who is due refreshes the counts, the queue and the profile chips together.
const QUEUE_TAGS = ['CallLists', 'CallListEntries', 'CallListMemberships'] as const;

const callListsApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({

    // An admin can pass a team member's account id to see their lists; everyone else gets their own.
    getCallLists: builder.query<CallList[], { ownerId?: string | null } | void>({
      query: (arg) => ({ url: '/call-lists', params: { owner_id: (arg && arg.ownerId) || undefined } }),
      providesTags: ['CallLists'],
    }),

    createCallList: builder.mutation<CallList, CreateCallListDto>({
      query: (data) => ({ url: '/call-lists', method: HTTPRequestType.POST, data }),
      invalidatesTags: ['CallLists'],
    }),

    updateCallList: builder.mutation<CallList, { listId: string; data: UpdateCallListDto }>({
      query: ({ listId, data }) => ({ url: `/call-lists/${listId}`, method: HTTPRequestType.PATCH, data }),
      // The panel's next-call previews read the list's settings.
      invalidatesTags: ['CallLists', 'CallListEntry'],
    }),

    deleteCallList: builder.mutation<void, { listId: string }>({
      query: ({ listId }) => ({ url: `/call-lists/${listId}`, method: HTTPRequestType.DELETE }),
      invalidatesTags: [...QUEUE_TAGS],
    }),

    getCallListSummary: builder.query<CallListSummary, { listId: string }>({
      query: ({ listId }) => ({ url: `/call-lists/${listId}/summary` }),
      providesTags: ['CallLists'],
    }),

    getCallListEntries: builder.query<PaginatedResponse<CallListEntry>, {
      listId: string;
      view: CallQueueView;
      search?: string;
      page: number;
      perPage: number;
    }>({
      query: ({ listId, view, search, page, perPage }) => ({
        url: `/call-lists/${listId}/entries`,
        params: { view, search: search || undefined, page, per_page: perPage },
      }),
      providesTags: ['CallListEntries'],
    }),

    getCallListEntry: builder.query<CallListEntryDetail, { listId: string; entryId: string }>({
      query: ({ listId, entryId }) => ({ url: `/call-lists/${listId}/entries/${entryId}` }),
      providesTags: (_result, _error, arg) => [{ type: 'CallListEntry', id: arg.entryId }],
    }),

    addCallListEntries: builder.mutation<AddCallListEntriesResult, { listId: string; data: AddCallListEntriesDto }>({
      query: ({ listId, data }) => ({ url: `/call-lists/${listId}/entries`, method: HTTPRequestType.POST, data }),
      invalidatesTags: [...QUEUE_TAGS],
    }),

    updateCallListEntry: builder.mutation<CallListEntry, { listId: string; entryId: string; data: UpdateCallListEntryDto }>({
      query: ({ listId, entryId, data }) => ({
        url: `/call-lists/${listId}/entries/${entryId}`,
        method: HTTPRequestType.PATCH,
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [...QUEUE_TAGS, { type: 'CallListEntry', id: arg.entryId }],
    }),

    removeCallListEntry: builder.mutation<EntryMoved, { listId: string; entryId: string }>({
      query: ({ listId, entryId }) => ({ url: `/call-lists/${listId}/entries/${entryId}`, method: HTTPRequestType.DELETE }),
      invalidatesTags: (_result, _error, arg) => [...QUEUE_TAGS, { type: 'CallListEntry', id: arg.entryId }],
    }),

    logCall: builder.mutation<LoggedCall, { listId: string; entryId: string; data: LogCallDto }>({
      query: ({ listId, entryId, data }) => ({
        url: `/call-lists/${listId}/entries/${entryId}/log`,
        method: HTTPRequestType.POST,
        data,
      }),
      // The call writes a note on the profile and maybe a follow-up task.
      invalidatesTags: (_result, _error, arg) => [
        ...QUEUE_TAGS,
        { type: 'CallListEntry', id: arg.entryId },
        'NotesFeed',
        'Comments',
        ...(arg.data.follow_up_task ? ['Tasks' as const] : []),
      ],
    }),

    skipCall: builder.mutation<EntryMoved, { listId: string; entryId: string }>({
      query: ({ listId, entryId }) => ({ url: `/call-lists/${listId}/entries/${entryId}/skip`, method: HTTPRequestType.POST }),
      invalidatesTags: (_result, _error, arg) => [...QUEUE_TAGS, { type: 'CallListEntry', id: arg.entryId }],
    }),

    deferCall: builder.mutation<EntryMoved, { listId: string; entryId: string }>({
      query: ({ listId, entryId }) => ({ url: `/call-lists/${listId}/entries/${entryId}/defer`, method: HTTPRequestType.POST }),
      invalidatesTags: (_result, _error, arg) => [...QUEUE_TAGS, { type: 'CallListEntry', id: arg.entryId }],
    }),

    getCallListMemberships: builder.query<CallListMemberships, { entityType: CallListPersonType; entityId: string }>({
      query: ({ entityType, entityId }) => ({
        url: '/call-lists/memberships',
        params: { entity_type: entityType, entity_id: entityId },
      }),
      providesTags: ['CallListMemberships'],
    }),

    // JT1-1827: agency-wide; the person drops off every active call list when it is set.
    setDoNotCall: builder.mutation<{ do_not_call: boolean }, { entityType: CallListPersonType; entityId: string; doNotCall: boolean }>({
      query: ({ entityType, entityId, doNotCall }) => ({
        url: `/do-not-call/${entityType}/${entityId}`,
        method: HTTPRequestType.PUT,
        data: { do_not_call: doNotCall },
      }),
      invalidatesTags: (_result, _error, arg) => [
        ...QUEUE_TAGS,
        'CallListEntry',
        arg.entityType === CallListPersonType.CONTACT
          ? { type: 'OrgContacts' as const, id: arg.entityId }
          : { type: 'OrgCandidates' as const, id: arg.entityId },
      ],
    }),

  }),
});

export const {
  useGetCallListsQuery,
  useCreateCallListMutation,
  useUpdateCallListMutation,
  useDeleteCallListMutation,
  useGetCallListSummaryQuery,
  useGetCallListEntriesQuery,
  useGetCallListEntryQuery,
  useAddCallListEntriesMutation,
  useUpdateCallListEntryMutation,
  useRemoveCallListEntryMutation,
  useLogCallMutation,
  useSkipCallMutation,
  useDeferCallMutation,
  useGetCallListMembershipsQuery,
  useSetDoNotCallMutation,
} = callListsApi;
